import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";

import { fetchStoryboardReportSummary, fetchReportsDataForDownload, invalidateDownloadData } from 'actions/storyboardReports';

import _ from 'lodash';
import dateformat from "dateformat";
import { Button } from "react-bootstrap";
import cx from 'classnames';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import StoryboardContext from 'components/Storyboard/context';

import { padNumber } from 'services/util';
import { convertDatesToTimezone } from 'services/report.service';

import { SingleTile, HorizontalTile, VerticalTile, LandingHorizontalTile } from '.';
import { CSVLink } from "react-csv";
import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_REPORTS_DOWNLOADED } from "constants/mixpanelEvents";

import styles from '../report.module.scss';

export function ReportSummary(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  const summary = useSelector(state => _.get(state, 'storyboards.storyboard.reports.summary.item', {}));
  const page = useSelector(state => _.get(state, 'storyboards.storyboard.item.page', {}));
  const published = useSelector(state => _.get(state, 'storyboards.storyboard.item.page.published', {}));

  useEffect(() => {
    var request = {
      storyboard: storyboardId
    }

    dispatch(fetchStoryboardReportSummary(request));
  }, []);

  const hasHoursTotalTime= () => {
    return Math.floor(summary.totalTimeSpent) >= 3600;
  };//checking for displaying the timetabel

  const hasHoursAverageTime = () => {
    return Math.floor(summary.averageTimeSpent) >= 3600;
  };//checking for displaying the timetabel

  const _renderTimeSpent = (seconds) => {
    let minutes = padNumber(Math.floor(seconds / 60), 2);
    seconds = padNumber(Math.floor(seconds % 60), 2);
    var hours = padNumber(Math.floor(minutes / 60), 2);
    minutes = padNumber(minutes % 60, 2);

    hours = hours > 0 ? `${hours}:` : ''
    return `${hours}${minutes}:${seconds}`;
  }

  return (
    <div className={styles.summary}>
      <div className={styles.summaryHeading}>
        <div className={styles.left}>
          <h1 className={styles.reportsHeading}>Reports</h1>
          <span className={styles.reportsDescription}>A telescopic view of how your content is performing</span>
        </div>

        <div className={styles.right}>
          <ExportCSV date={props.date} />
          <div className={styles.creator}>
            <span className={styles.heading}>Created by: </span>
            <span className={styles.value}>{_.get(page, 'createdBy.firstName') || ''} {_.get(page, 'createdBy.lastName') || ''}</span>
          </div>

          <div className={styles.updatedInfo}>
            {published && <>
              <div className={styles.published}>
                <span className={styles.heading}>Published: </span>
                <span className={styles.value}>{dateformat(page.lastPublished, 'dd mmm yyyy')}</span>
              </div>
              |
            </>
            }

            <div className={cx({ [styles.modified]: published })}>
              <span className={styles.heading}>Modified: </span>
              <span className={styles.value}>{dateformat(page.modifiedDate, 'dd mmm yyyy')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.summaryBody}>
        <div className={styles.tileContainer}>
          <div className={styles.left}>
            <VerticalTile
              className={styles.vertical}
              path={'/images/reports/sessions.svg'}
              title="Sessions"
              description="Recorded"
              value={summary.sessions}
              iconClassName={styles.sessionsIcon}
            />
            <VerticalTile
              className={styles.vertical}
              path={'/images/reports/visitors.svg'}
              title="Visitors"
              description="Attracted"
              value={summary.visitors}
              iconClassName={styles.visitorsIcon}
            />
            <VerticalTile
              className={styles.vertical}
              path={'/images/reports/leads.svg'}
              title="Leads"
              description="Captured"
              value={summary.leads}
              iconClassName={styles.leadsIcon}
            />
          </div>

          <div className={styles.right}>
            {props.storyboardType == "LANDING_PAGE" &&
              <div className={styles.landingTop}>
                <LandingHorizontalTile
                  path={'/images/reports/timespent.svg'}
                  title="Total Time"
                  description="Spent"
                  secondTitle="Average Time"
                  secondDesc="Spent"
                  timeLabelTotalTime={
                    hasHoursTotalTime(summary.totalTimeSpent)
                      ? 'HH:MM:SS'
                      : 'MM:SS'}
                  timeLabelAverageTime={
                    hasHoursAverageTime(summary.averageTimeSpent)
                      ? 'HH:MM:SS'
                      : 'MM:SS'}
                  secondValue={_renderTimeSpent(summary.averageTimeSpent)}
                  value={_renderTimeSpent(summary.totalTimeSpent)}
                  iconClassName={styles.timespentIcon}
                />
              </div>
            }
            {props.storyboardType == "INTERACTIVE_CONTENT" &&
              <div className={styles.top}>
                <HorizontalTile
                  path={'/images/reports/timespent.svg'}
                  title="Total Time"
                  description="Spent"
                  secondTitle="Average Time"
                  secondDesc="Spent"
                  timeLabelTotalTime={
                    hasHoursTotalTime(summary.totalTimeSpent)
                      ? 'HH:MM:SS'
                      : 'MM:SS'}
                  timeLabelAverageTime={
                    hasHoursAverageTime(summary.averageTimeSpent)
                      ? 'HH:MM:SS'
                      : 'MM:SS'}
                  secondValue={_renderTimeSpent(summary.averageTimeSpent)}
                  value={_renderTimeSpent(summary.totalTimeSpent)}
                  iconClassName={styles.timespentIcon}
                />
              </div>
            }
            {props.storyboardType == "INTERACTIVE_CONTENT" &&
              <div className={styles.bottom}>
                <SingleTile
                  path={'/images/reports/downloads.svg'}
                  title="Downloads"
                  description="Completed"
                  value={summary.downloads}
                  iconClassName={styles.leadsIcon}
                />
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

function ExportCSV(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  let downloads = useSelector(state => _.get(state, 'storyboards.storyboard.reports.download.items'));
  const completed = useSelector(state => _.get(state, 'storyboards.storyboard.reports.download.completed'));
  const progress = useSelector(state => _.get(state, 'storyboards.storyboard.reports.download.progress'));
  const [csvData, setCsvData] = useState([]);
  const csvLink = useRef();

  const headers = [
    { label: "Visitor", key: "visitor" },
    { label: "Email", key: "email" },
    { label: "Last Visited Date", key: "lastVisitedDate" },
    { label: "Source", key: "source" },
    { label: "Medium", key: "medium" },
    { label: "Page Landed on", key: "landedPage" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Country", key: "country" },
    { label: "Device", key: "device" },
    { label: "Browser", key: "browser" },
    { label: "Time Spent", key: "time_spent" },
    { label: "Completion", key: "completion" }
  ];

  const renderVisitorName = (data) => {
    var name = `${_.get(data, 'firstName') || ''} ${_.get(data, 'lastName') || ''} `;
    return name;
  }

  const renderLocation = (data, locationType) => {
    var city = _.get(data, 'country.city', '') || '';
    var country = _.get(data, 'country.country_name', '') || '';
    var region = _.get(data, 'country.region_name', '') || '';
    
    if (!city && !country && !region) {
      return '';
    }
    
    const locationMap = {
      city,
      country,
      region
    };

    return locationMap[locationType];
  }

  const renderTimeSpent = (seconds) => {
    let minutes = padNumber(Math.floor(seconds / 60), 2);
    seconds = padNumber(Math.floor(seconds % 60), 2);
    var hours = padNumber(Math.floor(minutes / 60), 2);
    minutes = padNumber(minutes % 60, 2);

    hours = hours > 0 ? `${hours}:` : ''
    return `${hours}${minutes}:${seconds}`;
  }
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  useEffect(() => {
    if (completed) {
      var columns = [];
      downloads && downloads.map((data, index) => {
        columns[index] = {
          visitor: renderVisitorName(data.visitor),
          email: data.visitor.email,
          lastVisitedDate: dateformat(data.lastVisited, 'dd mmm yyyy'),
          source: data.campaign && data.campaign.source,
          medium: data.campaign && data.campaign.medium,
          landedPage: data.url,
          city: renderLocation(data, 'city'),
          state: renderLocation(data, 'region'),
          country: renderLocation(data, 'country'),
          device: data.device,
          browser: data.browser,
          time_spent: renderTimeSpent(data.timeSpent),
          completion: `${Math.round(data.completion)} %`
        }
      });

    setCsvData(columns);
    }
  }, [completed]);

  useEffect(() => {
    if (!completed && csvData && csvData.length == 0) {
      return;
    }

    csvLink.current.link.click();

    return () => {
      dispatch(invalidateDownloadData());
    }
  }, [csvData]);

  const fetchReportsData = () => {
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      report_type: 'Master'  
    }
 
      dispatch(createMixpanelEvent(MIXPANEL_REPORTS_DOWNLOADED, mixpanelPayload));  
      dispatch(invalidateDownloadData());


    var request = {
      storyboard: storyboardId,
      ...convertDatesToTimezone(props.date),
    }

    dispatch(fetchReportsDataForDownload(request, 0, 50));
  }

  return (
    <div>
      <Button variant="primary" disabled={progress} onClick={() => fetchReportsData()} className={styles.download}>
        <span className={styles.name}>{progress ? "Downloading..." : "Download"}</span>
        <i className={cx("cleverstory-download", styles.downloadIcon)} />
      </Button>
      <CSVLink
        ref={csvLink}
        headers={headers}
        data={csvData}
        filename='Reports.csv'
        className='hidden'
      />
    </div>
  )
}